import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import {
  setBalanceSheet,
  setReportLoading,
  setReportError,
  setPrintData
} from '../../redux/accounting';

const BalanceSheet = () => {
  const dispatch = useDispatch();
  const { balanceSheet, reportsLoading, reportsError } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || []
  };

  const loadBalanceSheet = useCallback(async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    dispatch(setReportLoading({ reportType: 'balanceSheet', loading: true }));
    
    try {
      console.log('Loading balance sheet with Redux data:', reduxData);
      
      // Generate balance sheet from real data
      const data = await realDataAccountingService.generateRealBalanceSheet(
        currentSchool?.schoolID,
        asOfDate,
        reduxData
      );
      
      console.log('Balance sheet data received:', data);
      dispatch(setBalanceSheet(data));
    } catch (err) {
      console.error('Balance sheet error:', err);
      setError('Failed to load balance sheet: ' + err.message);
      dispatch(setReportError({ reportType: 'balanceSheet', error: err.message }));
    } finally {
      setLoading(false);
      dispatch(setReportLoading({ reportType: 'balanceSheet', loading: false }));
    }
  }, [currentSchool?.schoolID, asOfDate, reduxData, dispatch]);

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadBalanceSheet();
    }
  }, [currentSchool, asOfDate, loadBalanceSheet]);

  const handleRefresh = () => {
    loadBalanceSheet();
  };

  const handlePrint = () => {
    dispatch(setPrintData({ reportType: 'balanceSheet', data: balanceSheet }));
    // Print logic would be implemented here
  };

  const handleExport = async (format = 'pdf') => {
    try {
      const data = await accountingService.exportReport('balance-sheet', balanceSheet, format);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `balance_sheet_${asOfDate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const formatCurrency = (amount) => accountingService.formatCurrency(amount);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading balance sheet...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Balance Sheet
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="As of Date"
                type="date"
                value={asOfDate}
                onChange={(e) => setAsOfDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  disabled={!balanceSheet}
                >
                  Print
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('pdf')}
                  disabled={!balanceSheet}
                >
                  PDF
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={!balanceSheet}
                >
                  Excel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Balance Sheet */}
      {balanceSheet && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" align="center">
              {currentSchool?.schoolname || 'School'} Balance Sheet
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              As of {accountingService.formatDate(asOfDate)}
            </Typography>
          </Box>
          
          <Grid container spacing={0}>
            {/* Assets Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
                  ASSETS
                </Typography>
                
                {/* Current Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current Assets
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Cash and Cash Equivalents</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.cash || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Accounts Receivable</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.receivables || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Inventory</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.inventory || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Prepaid Expenses</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.currentAssets?.prepaid || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Current Assets</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.assets?.currentAssets || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Fixed Assets */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Fixed Assets
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Land and Buildings</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.landBuildings || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Equipment</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.equipment || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Furniture and Fixtures</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.assets?.fixedAssets?.furniture || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Less: Accumulated Depreciation</TableCell>
                          <TableCell align="right" sx={{ color: 'error.main' }}>
                            ({formatCurrency(balanceSheet.assets?.fixedAssets?.depreciation || 0)})
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Fixed Assets</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.assets?.fixedAssets || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Assets */}
                <Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 3, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>TOTAL ASSETS</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {formatCurrency(balanceSheet.assets?.totalAssets || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Grid>

            {/* Liabilities and Equity Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#d32f2f' }}>
                  LIABILITIES & EQUITY
                </Typography>
                
                {/* Current Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Current Liabilities
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Accounts Payable</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.payables || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Accrued Expenses</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.accrued || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Short-term Debt</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities?.shortTermDebt || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Current Liabilities</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.currentLiabilities || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Long-term Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Long-term Liabilities
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Long-term Debt</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.liabilities?.longTermLiabilities?.longTermDebt || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Long-term Liabilities</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.longTermLiabilities || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Liabilities */}
                <Box sx={{ mb: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>TOTAL LIABILITIES</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.liabilities?.totalLiabilities || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Equity */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Equity
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>Capital</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.equity?.capital || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Retained Earnings</TableCell>
                          <TableCell align="right">
                            {formatCurrency(balanceSheet.equity?.retainedEarnings || 0)}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ borderTop: 2, borderColor: 'primary.main' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Total Equity</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(balanceSheet.equity?.totalEquity || 0)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total Liabilities and Equity */}
                <Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow sx={{ borderTop: 3, borderColor: 'primary.main', backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>TOTAL LIABILITIES & EQUITY</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                            {formatCurrency((balanceSheet.liabilities?.totalLiabilities || 0) + (balanceSheet.equity?.totalEquity || 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {!balanceSheet && !loading && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No balance sheet data available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a date and click refresh to generate balance sheet
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default BalanceSheet;
